/**
 * Canonical Ad Status System — Single source of truth
 * ═══════════════════════════════════════════════════════
 * 6 canonical states:
 * - draft           → Saved by user, not public
 * - pending_review   → Awaiting admin/system review, not public
 * - active           → Approved / live / public
 * - hidden_by_user   → Manually hidden/paused by seller, not public
 * - sold             → Completed/sold, not public
 * - rejected         → Blocked by moderation, not public
 */

export const CANONICAL_STATUSES = [
  'draft',
  'pending_review',
  'active',
  'hidden_by_user',
  'sold',
  'rejected',
];

/**
 * Map legacy status values to canonical ones
 */
const LEGACY_STATUS_MAP = {
  published: 'active',
  under_review: 'pending_review',
  limited: 'rejected',
  hidden: 'hidden_by_user',
  hidden_auto: 'rejected',
  paused_by_seller: 'hidden_by_user',
  removed_by_moderation: 'rejected',
};

/**
 * Normalise any status string to its canonical form.
 */
export function normalizeStatus(raw) {
  if (!raw) return 'draft';
  const lower = raw.toLowerCase().trim();
  return LEGACY_STATUS_MAP[lower] || lower;
}

/**
 * Badge config: i18n label key, colour class, icon hint
 */
export const STATUS_BADGE = {
  active:         { labelKey: 'adStatus.active',        color: 'bg-emerald-500 text-white',  dot: 'bg-emerald-400' },
  draft:          { labelKey: 'adStatus.draft',         color: 'bg-gray-400 text-white',     dot: 'bg-gray-300' },
  pending_review: { labelKey: 'adStatus.pendingReview', color: 'bg-amber-500 text-white',    dot: 'bg-amber-400' },
  hidden_by_user: { labelKey: 'adStatus.hidden',        color: 'bg-slate-500 text-white',    dot: 'bg-slate-400' },
  sold:           { labelKey: 'adStatus.sold',          color: 'bg-blue-600 text-white',     dot: 'bg-blue-500' },
  rejected:       { labelKey: 'adStatus.rejected',      color: 'bg-red-600 text-white',      dot: 'bg-red-500' },
};

/**
 * Return badge info for a given (possibly legacy) status.
 */
export function getStatusBadge(rawStatus) {
  const canonical = normalizeStatus(rawStatus);
  return STATUS_BADGE[canonical] || STATUS_BADGE.draft;
}

/**
 * Tab definitions for the "My Ads" section.
 *
 * STRICT BUCKET CONTRACT (2026-06-01i, per user spec):
 *   All = Active + Under Review + Draft + Hidden
 *
 * `sold` lives in its OWN tab and is NOT included in `all`. `rejected` is
 * mapped to Hidden by LEGACY_STATUS_MAP. Any unknown status is dropped (and
 * logged on the backend) — it must not inflate the "All" count.
 */
const ALL_TAB_STATUSES = ['active', 'pending_review', 'draft', 'hidden_by_user'];

export const MY_ADS_TABS = [
  { key: 'all',     labelKey: 'myAds.tabs.all',           statuses: ALL_TAB_STATUSES },
  { key: 'active',  labelKey: 'adStatus.active',          statuses: ['active'] },
  { key: 'pending', labelKey: 'adStatus.pendingReview',   statuses: ['pending_review'] },
  { key: 'draft',   labelKey: 'adStatus.draft',           statuses: ['draft'] },
  { key: 'hidden',  labelKey: 'adStatus.hidden',          statuses: ['hidden_by_user'] },
  { key: 'sold',    labelKey: 'adStatus.sold',            statuses: ['sold'] },
];

/**
 * Filter an array of ads by a tab key.
 * Strict: `all` returns only Active + Pending + Draft + Hidden.
 */
export function filterAdsByTab(ads, tabKey) {
  const tab = MY_ADS_TABS.find((t) => t.key === tabKey);
  if (!tab) return ads;
  return ads.filter((ad) => {
    const canonical = normalizeStatus(ad.status);
    return tab.statuses.includes(canonical);
  });
}

/**
 * Count ads per tab — returns `{ all: N, active: N, pending: N, ... }`.
 * The `all` bucket is the total of the input array (caller has already
 * excluded deleted ads).
 */
export function countAdsByTab(ads) {
  const counts = {};
  MY_ADS_TABS.forEach((tab) => {
    counts[tab.key] = 0;
  });
  const list = ads || [];
  // Strict bucket contract: every tab (including `all`) is computed by
  // status matching. `all` is no longer just `list.length` because that
  // would count sold/rejected/unknown ads — violating the user contract
  // All = Active + Under Review + Draft + Hidden.
  list.forEach((ad) => {
    const canonical = normalizeStatus(ad.status);
    MY_ADS_TABS.forEach((tab) => {
      if (tab.statuses && tab.statuses.includes(canonical)) {
        counts[tab.key] += 1;
      }
    });
  });
  return counts;
}

/**
 * Available card actions per canonical status.
 * Each action: { key, label, variant, confirm? }
 */
export function getCardActions(rawStatus, isOwner = false) {
  if (!isOwner) return [];
  const canonical = normalizeStatus(rawStatus);

  switch (canonical) {
    case 'draft':
      return [
        { key: 'edit',    label: 'Edit' },
        { key: 'publish', label: 'Submit for Review', variant: 'primary' },
        { key: 'delete',  label: 'Delete', variant: 'destructive', confirm: true },
      ];
    case 'pending_review':
      return [
        { key: 'view', label: 'View' },
      ];
    case 'active':
      return [
        { key: 'edit',      label: 'Edit' },
        { key: 'hide',      label: 'Hide', variant: 'secondary' },
        { key: 'mark_sold', label: 'Mark as Sold', variant: 'secondary' },
        { key: 'delete',    label: 'Delete', variant: 'destructive', confirm: true },
      ];
    case 'hidden_by_user':
      return [
        { key: 'restore', label: 'Restore', variant: 'primary' },
        { key: 'edit',    label: 'Edit' },
        { key: 'delete',  label: 'Delete', variant: 'destructive', confirm: true },
      ];
    case 'sold':
      return [
        { key: 'republish', label: 'Republish', variant: 'primary' },
        { key: 'delete',    label: 'Delete', variant: 'destructive', confirm: true },
        { key: 'view',      label: 'View' },
      ];
    case 'rejected':
      return [
        { key: 'view_reason', label: 'View Reason' },
        { key: 'edit_resubmit', label: 'Edit & Resubmit', variant: 'primary' },
        { key: 'delete', label: 'Delete', variant: 'destructive', confirm: true },
      ];
    default:
      return [];
  }
}

export default {
  CANONICAL_STATUSES,
  normalizeStatus,
  getStatusBadge,
  STATUS_BADGE,
  MY_ADS_TABS,
  filterAdsByTab,
  countAdsByTab,
  getCardActions,
};
